import { useEffect, useState } from "react";
import BrandMark from "@/components/landing/BrandMark";

const MESSAGES = [
  "Preparing your digital rewards…",
  "Loading loyalty cards…",
  "Getting your customers ready to return…",
  "Almost ready to serve…",
];

const ROUTE_D = "M12,54 C56,54 56,20 104,20 S168,22 182,15";

/**
 * Full-screen branded loading overlay.
 * Stays visible until `ready` (all essential sections + fonts) AND a minimum
 * animation time have elapsed, then completes the bar, shows "Ready to serve",
 * and fades out. Provides an "Enter website" escape after 10s and a Retry path
 * on error so visitors are never trapped.
 */
export default function Loader({ ready, error, onFinish, onRetry }) {
  const [progress, setProgress] = useState(0);
  const [msgIdx, setMsgIdx] = useState(0);
  const [showEnter, setShowEnter] = useState(false);
  const [minElapsed, setMinElapsed] = useState(false);
  const [phase, setPhase] = useState("loading"); // loading -> done -> fadeout
  const done = phase === "done" || phase === "fadeout";

  // Progress creep while loading (eases toward 90%)
  useEffect(() => {
    if (phase !== "loading") return;
    const id = setInterval(() => {
      setProgress((p) => (p < 90 ? Math.min(90, p + Math.random() * 8 + 2) : p));
    }, 180);
    return () => clearInterval(id);
  }, [phase]);

  // Rotating messages
  useEffect(() => {
    if (phase !== "loading") return;
    const id = setInterval(() => setMsgIdx((i) => (i + 1) % MESSAGES.length), 1600);
    return () => clearInterval(id);
  }, [phase]);

  // Minimum on-screen time so the branded animation is seen
  useEffect(() => {
    const t = setTimeout(() => setMinElapsed(true), 1500);
    return () => clearTimeout(t);
  }, []);

  // "Enter website" escape hatch after 10s
  useEffect(() => {
    const t = setTimeout(() => setShowEnter(true), 10000);
    return () => clearTimeout(t);
  }, []);

  // Complete when content is ready and the min time has passed
  useEffect(() => {
    if (ready && minElapsed && phase === "loading") {
      setProgress(100);
      setPhase("done");
    }
  }, [ready, minElapsed, phase]);

  // done -> hold "Ready to serve" -> fade out -> finish
  useEffect(() => {
    if (phase === "done") {
      const t = setTimeout(() => setPhase("fadeout"), 850);
      return () => clearTimeout(t);
    }
    if (phase === "fadeout") {
      const t = setTimeout(() => onFinish && onFinish(), 480);
      return () => clearTimeout(t);
    }
  }, [phase, onFinish]);

  const forceEnter = () => {
    setProgress(100);
    setPhase("done");
  };

  return (
    <div
      data-testid="app-loader"
      role="dialog"
      aria-modal="true"
      aria-label="Loading Street Eats Rewards"
      className={`fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-[#07182D] px-6 transition-opacity duration-500 ${
        phase === "fadeout" ? "opacity-0" : "opacity-100"
      }`}
    >
      <div className="absolute inset-0 grid-lines opacity-20" aria-hidden />
      <div
        className="absolute w-[420px] h-[420px] rounded-full bg-[#D4A017]/10 blur-[120px]"
        aria-hidden
      />

      {/* Logo fades into center */}
      <div
        className="relative z-10"
        style={{ animation: "ldr-logo .7s ease .2s both" }}
      >
        <BrandMark size={72} />
      </div>

      {/* Route travelling toward the location pin */}
      <div className="relative z-10 mt-6 w-[240px] max-w-full" aria-hidden>
        <svg viewBox="0 0 200 70" className="w-full">
          {/* dashed route */}
          <path
            className="ldr-route-el"
            d={ROUTE_D}
            fill="none"
            stroke="#D4A017"
            strokeOpacity="0.45"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeDasharray="4 8"
            style={{ animation: "ldr-dash 1s linear infinite" }}
          />
          {/* travelling truck */}
          <g
            className="ldr-truck-el"
            style={{
              offsetPath: `path("${ROUTE_D}")`,
              offsetRotate: "0deg",
              animation: "ldr-truck 1.9s ease-in-out infinite",
            }}
          >
            <g transform="translate(-8,-5)">
              <rect x="0" y="0.5" width="13" height="8" rx="1.6" fill="#D4A017" />
              <rect x="9.5" y="2.5" width="5" height="6" rx="1" fill="#F5C542" />
              <circle cx="3.5" cy="9.5" r="1.6" fill="#FFFFFF" />
              <circle cx="11" cy="9.5" r="1.6" fill="#FFFFFF" />
            </g>
          </g>
          {/* destination pin */}
          <g
            className="ldr-pin-el"
            style={{
              transformBox: "fill-box",
              transformOrigin: "50% 100%",
              animation: "ldr-pin .6s cubic-bezier(.34,1.56,.64,1) 1s both",
            }}
          >
            <path
              d="M182,4 c-5.2,0 -9.4,4.2 -9.4,9.4 c0,7 9.4,15 9.4,15 c0,0 9.4,-8 9.4,-15 C191.4,8.2 187.2,4 182,4 z"
              fill="#D4A017"
            />
            <circle cx="182" cy="13.4" r="3.4" fill="#07182D" />
          </g>
        </svg>
      </div>

      {/* Headline */}
      <h1 className="relative z-10 mt-7 font-display uppercase text-2xl sm:text-3xl text-center text-white tracking-tight leading-[0.95] max-w-xs">
        Your rewards experience
        <br />
        is on the way.
      </h1>

      {/* Progress bar */}
      <div className="relative z-10 mt-8 w-[280px] max-w-full">
        <div
          className="h-2 bg-white/10 border border-white/15 overflow-hidden"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={done ? 100 : Math.round(progress)}
        >
          <div
            data-testid="loader-progress-fill"
            className="h-full bg-[#D4A017] transition-[width] duration-300 ease-out"
            style={{ width: `${done ? 100 : progress}%` }}
          />
        </div>
        <div className="mt-2.5 flex items-center justify-between font-mono text-[10px] uppercase tracking-widest text-white/55">
          <span data-testid="loader-message" aria-live="polite">
            {done ? "Ready to serve" : MESSAGES[msgIdx]}
          </span>
          <span data-testid="loader-percent">
            {done ? 100 : Math.round(progress)}%
          </span>
        </div>
      </div>

      {/* Escape hatch after 10s */}
      {showEnter && phase === "loading" && !error && (
        <button
          type="button"
          onClick={forceEnter}
          data-testid="loader-enter"
          className="relative z-10 mt-7 btn-brutal bg-transparent text-white border-2 border-white/40 hover:border-[#D4A017] hover:text-[#D4A017] font-display uppercase tracking-wide px-6 py-2.5 text-sm"
        >
          Enter website
        </button>
      )}

      {/* Error / retry */}
      {error && (
        <div className="relative z-10 mt-7 text-center" data-testid="loader-error">
          <p className="font-mono text-xs uppercase tracking-widest text-white/70">
            Having trouble loading?
          </p>
          <button
            type="button"
            onClick={onRetry}
            data-testid="loader-retry"
            className="mt-3 btn-brutal bg-[#D4A017] text-[#07182D] border-2 border-[#D4A017] font-display uppercase tracking-wide px-6 py-2.5 text-sm shadow-hard-sm"
          >
            Retry
          </button>
        </div>
      )}
    </div>
  );
}
